// The applicant's side of a join request: the wall they hit while they're blocked, and
// the "you can ask again" screen once they aren't.
//
// Four states share one page because they are four sentences about the same thing, and
// splitting them into four routes would mean four places to keep the copy honest:
//
//   pending    — asked, nobody has decided, no access.
//   locked_out — approved, trial ran out, still no seat, no access.
//   rejected   — turned down; can ask again after the 7-day cooldown.
//   lapsed     — nobody acted for 30 days; same cooldown, same re-request.
//
// `approved_trial` is NOT here: that applicant is using the product on trial, so they
// get a banner (ApprovedTrialBanner), not a wall.
import React, { useState } from 'react';
import { Clock, Lock, RefreshCw, XCircle } from 'lucide-react';
import { T } from '../gamification/shared';
import { createLogger } from '../../utils/logger';
import { useAuth } from '../../contexts/AuthContext';
import { JoinRequestStatus, createJoinRequest } from '../../services/joinRequestService';
import { useMyJoinRequest } from './useMyJoinRequest';

const logger = createLogger('JoinRequestStatusPage');

/** Absolute dates throughout. "In 6 days" read a day later is wrong, and the whole point
 *  of the cooldown message is telling someone the day they can come back. */
function formatDate(iso?: string | null): string {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
}

interface Copy {
    icon: React.ReactNode;
    accent: string;
    title: string;
    body: string;
    detail?: string;
}

function copyFor(
    status: JoinRequestStatus,
    orgName: string,
    expiresAt?: string | null,
    cooldownEnd?: string | null,
    canReRequest?: boolean
): Copy {
    switch (status) {
        case 'pending':
            return {
                icon: <Clock size={30} />,
                accent: '#0f5f8f',
                title: 'Waiting for approval',
                body: `Your request to join ${orgName} is waiting for approval.`,
                detail: expiresAt
                    ? `A Trainer at ${orgName} has until ${formatDate(expiresAt)} to review it. We'll email you as soon as they decide.`
                    : `We'll email you as soon as a Trainer decides.`,
            };
        case 'locked_out':
            return {
                icon: <Lock size={30} />,
                accent: '#b45309',
                title: 'Waiting for a seat',
                body: `Your trial ended and no seat at ${orgName} is free yet.`,
                detail: `${orgName}'s admin has been notified and can assign you a seat. You'll get an email the moment they do.`,
            };
        case 'rejected':
            return {
                icon: <XCircle size={30} />,
                accent: '#b91c1c',
                title: 'Request not approved',
                body: `Your request to join ${orgName} wasn't approved.`,
                detail: canReRequest
                    ? 'You can send a new request now, or reach out to your organization’s admin directly.'
                    : cooldownEnd
                        ? `You can try again from ${formatDate(cooldownEnd)}, or reach out to your organization’s admin directly.`
                        : 'You can reach out to your organization’s admin directly.',
            };
        case 'lapsed':
        default:
            return {
                icon: <Clock size={30} />,
                accent: '#6b7280',
                title: 'Request expired',
                body: `Your request to join ${orgName} expired without a response.`,
                detail: canReRequest
                    ? 'You can send a new request now.'
                    : cooldownEnd
                        ? `You can try again from ${formatDate(cooldownEnd)}.`
                        : 'You can send a new request shortly.',
            };
    }
}

const JoinRequestStatusPage: React.FC = () => {
    const { logout } = useAuth();
    const { request, refresh } = useMyJoinRequest();
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const status = (request.status ?? 'pending') as JoinRequestStatus;
    const orgName = request.organization_name ?? 'that organization';
    const isClosed = status === 'rejected' || status === 'lapsed';

    const copy = copyFor(
        status,
        orgName,
        request.expires_at,
        request.can_re_request_at,
        request.can_re_request
    );

    const handleReRequest = async () => {
        setSubmitting(true);
        setError(null);
        try {
            await createJoinRequest();
            // Re-read rather than trusting the response: the new row's own window and
            // cooldown are what the next render must show, and the server owns both.
            refresh();
        } catch (err) {
            // The API's own message is the useful one here — it carries the exact date
            // the cooldown lifts, which nothing on the client can recompute.
            const message = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
            logger.warn('Re-request failed', err);
            setError(message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: T.bg,
                fontFamily: T.font,
                padding: 24,
            }}
        >
            <div
                style={{
                    background: T.card,
                    border: `1px solid ${T.border}`,
                    borderRadius: T.radius,
                    boxShadow: T.shadow,
                    maxWidth: 460,
                    width: '100%',
                    padding: '36px 32px',
                    textAlign: 'center',
                }}
            >
                <div style={{ color: copy.accent, display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                    {copy.icon}
                </div>

                <h1 style={{ margin: '0 0 10px', fontSize: '1.4rem', fontWeight: 700, color: T.text }}>
                    {copy.title}
                </h1>

                <p style={{ margin: '0 0 10px', fontSize: '0.95rem', color: T.text, lineHeight: 1.6 }}>
                    {copy.body}
                </p>

                {copy.detail && (
                    <p style={{ margin: '0 0 22px', fontSize: '0.875rem', color: T.muted, lineHeight: 1.7 }}>
                        {copy.detail}
                    </p>
                )}

                {error && (
                    <div
                        style={{
                            background: '#fef2f2',
                            border: '1px solid #fca5a5',
                            borderRadius: 8,
                            padding: '10px 14px',
                            marginBottom: 18,
                            fontSize: '0.8125rem',
                            color: '#991b1b',
                            lineHeight: 1.5,
                        }}
                    >
                        {error}
                    </div>
                )}

                {isClosed && request.can_re_request && (
                    <button
                        onClick={handleReRequest}
                        disabled={submitting}
                        style={{
                            width: '100%',
                            padding: '11px 20px',
                            marginBottom: 10,
                            borderRadius: 8,
                            border: 'none',
                            background: T.primary,
                            color: '#fff',
                            fontFamily: T.font,
                            fontSize: '0.9rem',
                            fontWeight: 600,
                            cursor: submitting ? 'not-allowed' : 'pointer',
                            opacity: submitting ? 0.6 : 1,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 8,
                        }}
                    >
                        <RefreshCw size={15} />
                        {submitting ? 'Sending…' : 'Send a new request'}
                    </button>
                )}

                <button
                    onClick={() => logout()}
                    style={{
                        width: '100%',
                        padding: '11px 20px',
                        borderRadius: 8,
                        border: `1px solid ${T.border}`,
                        background: T.card,
                        color: T.muted,
                        fontFamily: T.font,
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                    }}
                >
                    Log out
                </button>
            </div>
        </div>
    );
};

export default JoinRequestStatusPage;
